import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import type { ProductResponseDTO } from '@libs/dtos/product/product.dto';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { PlatformService } from '@services/platforms/platform.service';
import { ProductService } from '@services/products/product.service';
import { ConfirmDialogService } from '@services/ui/confirm-dialog.service';
import { ValidationService } from '@services/ui/validation.service';
import { BrandIconComponent } from '@shared/components/brand-icon/brand-icon.component';
import type { BrandIconName } from '@shared/components/brand-icon/brand-icons';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { InputComponent } from '@shared/components/input/input.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { SharedModule } from '@shared/modules/shared.module';
import { platformBrandColor, platformBrandIcon } from 'app/core/constants/platform-brand.constants';
import { translateApiError } from 'app/core/utils/api-error';
import { LucideAngularModule } from 'lucide-angular';
import { firstError, maxNotBelowMinValidator, optionalPositiveInt } from '../product-form.rules';

@Component({
	selector: 'app-product-detail',
	imports: [
		BrandIconComponent,
		SharedModule,
		ButtonComponent,
		SpinnerComponent,
		InputComponent,
		SelectComponent,
		CheckboxComponent,
		LucideAngularModule,
	],
	templateUrl: './product-detail.component.html',
	styleUrl: './product-detail.component.scss',
})
export class ProductDetailComponent implements OnInit {
	product: ProductResponseDTO | null = null;
	loadingDetails = false;
	platformName: string = '';
	productId: string = '';
	isEditing = false;
	changingProvider = false;
	saving = false;
	editForm!: FormGroup;
	platforms: { label: string; value: number }[] = [];

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private fb: FormBuilder,
		private translocoService: TranslocoService,
		private productService: ProductService,
		private platformService: PlatformService,
		private messageAlertService: MessageAlertService,
		private confirmDialogService: ConfirmDialogService,
		private validationService: ValidationService,
	) {
		this.initForm();
	}

	ngOnInit(): void {
		this.productId = this.route.snapshot.paramMap.get('id') || '';
		if (this.productId) {
			this.loadProductDetails();
			this.loadPlatforms();
		} else {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('productsCrud.messages.noValidProductId'),
			);
			this.router.navigate(['/configuration']);
		}
	}

	/** Same rules as the create/edit dialog (product-form.rules). The name is edited in the dialog, not here */
	initForm(): void {
		this.editForm = this.fb.group(
			{
				platformId: [null, Validators.required],
				minAmount: [null, optionalPositiveInt],
				maxAmount: [null, optionalPositiveInt],
				averageTime: [null, optionalPositiveInt],
				requiresCustomComments: [false],
			},
			{ validators: [maxNotBelowMinValidator()] },
		);
	}

	loadProductDetails(): void {
		this.loadingDetails = true;
		this.productService.getProductById(this.productId).subscribe({
			next: (fullProduct) => {
				this.product = fullProduct;
				this.loadPlatformName(fullProduct.platformId);
				this.populateForm();
				this.loadingDetails = false;
			},
			error: () => {
				this.loadingDetails = false;
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('productsCrud.messages.loadError'),
				);
				this.router.navigate(['/configuration']);
			},
		});
	}

	loadPlatforms(): void {
		this.platformService.getAllPlatforms().subscribe({
			next: (platforms) => {
				this.platforms = platforms.map((p) => ({ label: p.name, value: p.id }));
			},
			error: () => {
				this.platforms = [];
			},
		});
	}

	populateForm(): void {
		if (this.product) {
			this.editForm.reset({
				platformId: this.product.platformId,
				minAmount: this.product.minAmount,
				maxAmount: this.product.maxAmount,
				averageTime: this.product.averageTime,
				requiresCustomComments: this.product.requiresCustomComments,
			});
		}
	}

	loadPlatformName(platformId: number): void {
		if (platformId === null || platformId === undefined) return;
		// From the shared catalog (already loaded for the form), not one more request
		this.platformService.getAllPlatforms().subscribe({
			next: (platforms) => {
				const platform = platforms.find((p) => p.id.toString() === platformId.toString());
				this.platformName =
					platform?.name ??
					this.translocoService.translate('productsCrud.messages.platformFallback', { id: platformId });
			},
			error: () => {
				this.platformName = this.translocoService.translate('productsCrud.messages.platformFallback', {
					id: platformId,
				});
			},
		});
	}

	onEdit(): void {
		this.isEditing = true;
		this.populateForm();
	}

	onCancelEdit(): void {
		this.isEditing = false;
		this.populateForm(); // Reset form to original values
	}

	onSave(): void {
		if (this.saving) return; // double click
		if (this.editForm.invalid) {
			this.editForm.markAllAsTouched(); // field errors under each input
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('productsCrud.messages.completeRequired'),
			);
			return;
		}

		const updatedProduct = {
			...this.editForm.value,
			id: this.productId,
		};

		this.saving = true;
		this.productService.updateProduct(this.productId, updatedProduct).subscribe({
			next: () => {
				this.saving = false;
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate('common.labels.success'),
					this.translocoService.translate('productsCrud.messages.updateSuccess'),
				);
				this.isEditing = false;
				this.loadProductDetails();
			},
			error: (err) => {
				this.saving = false;
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					translateApiError(err, this.translocoService, 'productsCrud.messages.updateError'),
				);
			},
		});
	}

	/** Touched and invalid; max also when it is below min (a form-level error) */
	isFieldInvalid(field: string): boolean {
		const control = this.editForm.get(field);
		if (!control?.touched) return false;
		return control.invalid || (field === 'maxAmount' && this.editForm.hasError('maxBelowMin'));
	}

	getErrorMessage(field: string): string {
		const control = this.editForm.get(field);
		const error = firstError(control?.errors) ?? (field === 'maxAmount' ? firstError(this.editForm.errors) : null);
		return error ? this.validationService.getValidatorErrorMessage(error.key, error.params) : '';
	}

	onDelete(): void {
		this.confirmDialogService.confirmAndExecute(
			'delete',
			this.translocoService.translate('productsCrud.messages.deleteTarget', { name: this.product?.name ?? '' }),
			() => this.productService.deleteProduct(this.productId),
			{
				onSuccess: () => {
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						this.translocoService.translate('productsCrud.messages.deleteSuccess'),
					);
					this.router.navigate(['/configuration']);
				},
				errorMessage: this.translocoService.translate('productsCrud.messages.deleteError'),
			},
		);
	}

	goBack(): void {
		this.router.navigate(['/configuration']);
	}

	/** Platform name: from the platforms list already loaded, the single-platform call as fallback */
	get platformLabel(): string {
		const id = this.product?.platformId;
		const fromList = this.platforms.find((p) => String(p.value) === String(id))?.label;
		return fromList || this.platformName;
	}

	get platformIcon(): BrandIconName | null {
		return platformBrandIcon(this.platformLabel);
	}

	get platformColor(): string {
		return platformBrandColor(this.platformLabel);
	}

	getActiveProviderName(): string {
		const activeProvider = this.product?.activeProvider || this.product?.providers?.find((p) => p.isActive);
		return activeProvider?.providerName || this.translocoService.translate('productsCrud.detail.noProvider');
	}

	getActiveProviderCode(): string {
		const activeProvider = this.product?.activeProvider || this.product?.providers?.find((p) => p.isActive);
		return activeProvider?.code || '-';
	}

	getActiveProvider(): string {
		const activeProvider = this.product?.activeProvider || this.product?.providers?.find((p) => p.isActive);
		if (activeProvider) {
			return `${activeProvider.providerName || this.translocoService.translate('productsCrud.messages.providerFallback')} (${activeProvider.code})`;
		}
		return this.translocoService.translate('products.detail.fields.noProvider');
	}

	/**
	 * Makes another provider the active one. It is an immediate action with its own confirmation, not part
	 * of the edit form (Cancel there could not undo it), so it is offered only outside edit mode
	 */
	confirmSetActiveProvider(provider: { id?: number; providerName?: string; code?: string }): void {
		const productProviderId = provider.id;
		if (this.isEditing || this.changingProvider || productProviderId === undefined) return;
		this.confirmDialogService.confirmCustom({
			header: this.translocoService.translate('productsCrud.detail.setActiveHeader'),
			message: this.translocoService.translate('productsCrud.detail.setActiveConfirm', {
				name:
					provider.providerName || this.translocoService.translate('productsCrud.messages.providerFallback'),
				code: provider.code || '-',
			}),
			accept: () => this.onSetActiveProvider(productProviderId),
		});
	}

	private onSetActiveProvider(productProviderId: number): void {
		this.changingProvider = true;
		this.productService.setActiveProvider(this.productId, productProviderId.toString()).subscribe({
			next: () => {
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate('common.labels.success'),
					this.translocoService.translate('productsCrud.messages.updateSuccess'),
				);
				this.loadProductDetails();
				this.changingProvider = false;
			},
			error: (err) => {
				this.changingProvider = false;
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					translateApiError(err, this.translocoService, 'productsCrud.messages.updateError'),
				);
			},
		});
	}

	getInactiveProviders(): ProductResponseDTO['providers'] {
		if (!this.product?.providers) return [];
		return this.product.providers.filter((p) => !p.isActive);
	}

	formatTime(minutes: number | null | undefined): string {
		if (!minutes) return '-';
		if (minutes < 60) {
			return `${minutes} min`;
		}
		const hours = Math.floor(minutes / 60);
		const remainingMinutes = minutes % 60;
		return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
	}
}
